import {BaseElement, MsgLabel, OSkinableInit} from "back/commons/basis";
import {WSP, Wsp} from "lib/wsp/wsp";
import {JSrcFields} from "lib/wsp/src";
import {DOMSH} from "lib/commons/xml/domsh";
import {DOM, JSX} from "lib/commons/xml/dom";
import {REG} from "lib/commons/registry";

export interface OCodeViewInit extends OSkinableInit {
	uiClass?: "partial"
}

export class CodeView extends BaseElement {

	protected config: OCodeViewInit;

	protected _wsp: Wsp;

	protected _fields: JSrcFields;

	protected _transform: string;

	protected _pending: MsgLabel;

	protected _rootCode: HTMLDivElement;

	get wsp(): Wsp {return this._wsp || this.findReg(this.config).env.wsp}

	set wsp(v: Wsp) {
		this._wsp = v;
		this._fields = null;
		this.refresh();
	}

	get fields(): JSrcFields {return this._fields || this.findReg(this.config).env.longDesc}

	set fields(v: JSrcFields) {
		this._fields = v;
		this.refresh();
	}

	get transform(): string {return this._transform}

	set transform(v: string) {
		this._transform = v;
		this.refresh();
	}

	protected async _initialize(init: OCodeViewInit) {
		this.config = init;
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin(this.localName, init);

		this._pending = sr.appendChild(<MsgLabel/>) as MsgLabel;
		this._rootCode = sr.appendChild(<div class="itemPreview"/>) as HTMLDivElement;
		if (init.uiClass) this._rootCode.classList.add(init.uiClass);
	}

	protected async _refresh() {
		this._pending.setStandardMsg("loading");
		DOM.setHidden(this._rootCode, true);
		// URL fournissant le fragment XHTML de code
		const itemType = await this.wsp.wspMetaUi.getItemType(this.fields.itModel);
		this._rootCode.innerHTML = "";
		// Flux xhtml associé
		const txt = await WSP.fetchStreamText(this.wsp, null, itemType.getMainStreamSrcUri(this.fields), this.transform);
		if (txt) {
			this._rootCode.innerHTML = txt;
			this._rootCode.appendChild(<div class="fadeOut"/>);
		}
		DOM.setHidden(this._pending, true);
		DOM.setHidden(this._rootCode, false);
	}


}

REG.reg.registerSkin('wsp-code-view', 1, /* language=CSS */ `
	:host {
		flex-direction: column;
		justify-content: center;
		text-align: center;
		vertical-align: middle;
		overflow: auto;
	}

	.partial {
		border: 1px solid #E1E1D7;
		position: relative;
		overflow: hidden !important;
		padding: 1px 2px;
	}

	.itemPreview {
		overflow-y: auto;
		background-color: white;
		font-size: 80%;
		cursor: pointer;
	}

	.fadeOut {
		display: none;
		z-index: 2;
	}

	.partial > .fadeOut {
		display: block;
		position: absolute;
		height: 8em;
		bottom: 0;
		left: 0;
		right: 0;
		background: -moz-linear-gradient(top, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0) 75%, rgba(255, 255, 255, 1) 100%);
		background: linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0) 75%, rgba(255, 255, 255, 1) 100%);
	}

	/* === CodeMirror =========================================================== */
	.CodeMirror-static {
	}

	.CodeMirror-static pre {
		/* Reset some styles that the rest of the page might have set */
		border-radius: 0;
		border-width: 0;
		background: transparent;
	  font-family: inherit;
	  font-size: inherit;
	  margin: 0;
	  white-space: pre-wrap;
	  word-wrap: normal;
	  line-height: inherit;
	  color: inherit;
	  z-index: 2;
	  position: relative;
	  overflow: visible;
	  border-inline-end: 30px solid transparent;
	  width: -moz-fit-content;
	  width: fit-content;
	  font-family: var(--font-mono);
	  tab-size: 2;
	  -moz-tab-size: 2;
  }

  .CodeMirror-line {
	  position: relative;
  }

	.cm-keyword {
		color: #708;
	}

	.cm-atom {
		color: #219;
	}

	.cm-number {
		color: #164;
	}

	.cm-def {
		color: #00f;
	}

	.cm-variable {
		color: black;
	}

	.cm-variable-2 {
		color: #05a;
	}

	.cm-variable-3 {
		color: #085;
	}

	.cm-property {
		color: black;
	}

	.cm-operator {
		color: black;
	}

	.cm-comment {
		color: #a50;
	}

	.cm-string {
		color: #a11;
	}

	.cm-string-2 {
		color: #f50;
	}

	.cm-meta {
		color: #555;
	}

	.cm-error {
		color: #f00;
	}

	.cm-qualifier {
		color: #555;
	}

	.cm-builtin {
		color: #30a;
	}

	.cm-bracket {
		color: #997;
	}

	.cm-tag {
		color: #170;
	}

	.cm-attribute {
		color: #00c;
	}

	.cm-header {
		color: blue;
	}

	.cm-quote {
		color: #090;
	}

	.cm-hr {
		color: #999;
	}

	.cm-link {
		color: #00c;
	}

	.cm-negative {
		color: #d44;
	}

	.cm-positive {
		color: #292;
	}

	.cm-header, .cm-strong {
		font-weight: bold;
	}

	.cm-em {
		font-style: italic;
	}

	.cm-link {
		text-decoration: underline;
	}

	.cm-invalidchar {
		color: #f00;
	}

	/* === STOP - CodeMirror ==================================================== */
`);

customElements.define('wsp-code-view', CodeView);

const DEBUG = true;
